<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CAPTAIN_ERROR_TYPES } from 'dashboard/composables/captain/constants';
import TasksAPI from 'dashboard/api/captain/tasks';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from 'dashboard/components-next/captain/assistant/MessageList.vue';
import CopilotInput from 'dashboard/components-next/copilot/CopilotInput.vue';

// The template as the form holds it, in Captain::TemplateDraftSchema's shape. It goes with every turn,
// so whatever the admin changed by hand is what the next draft starts from.
const props = defineProps({
  template: { type: Object, required: true },
});

// busy: whether a turn is in flight, so the page keeps the chat open until its draft lands.
const emit = defineEmits(['apply', 'busy']);

const { t } = useI18n();

const messages = ref([]);
const isLoading = ref(false);
watch(isLoading, busy => emit('busy', busy));
// Why the last turn got no answer (the provider out of credits, Captain off...). It stays in the chat
// until the next try: an alert that fades leaves a request with no reply and no reason.
const failure = ref('');
let controller = null;
const ABORTED = [
  CAPTAIN_ERROR_TYPES.ABORT_ERROR,
  CAPTAIN_ERROR_TYPES.CANCELED_ERROR,
];

const suggestions = computed(() => [
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.SUGGESTION_PAYMENT'),
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.SUGGESTION_APPOINTMENT'),
  t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.SUGGESTION_OFFER'),
]);

const requestDraft = async () => {
  isLoading.value = true;
  failure.value = '';
  controller = new AbortController();
  try {
    const { data } = await TasksAPI.templateDraft(
      {
        messages: messages.value.map(message => ({
          role: message.sender,
          content: message.content,
        })),
        template: props.template,
      },
      controller.signal
    );
    messages.value.push({ sender: 'assistant', content: data.message.reply });
    emit('apply', data.message.template);
  } catch (error) {
    if (ABORTED.includes(error.name)) return;
    failure.value =
      error.response?.data?.error ||
      t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const send = content => {
  if (isLoading.value) return;
  messages.value.push({ sender: 'user', content });
  requestDraft();
};

onBeforeUnmount(() => controller?.abort());
</script>

<template>
  <section class="flex flex-col h-[36rem] overflow-hidden">
    <div
      v-if="!messages.length"
      class="flex flex-col justify-center flex-1 gap-4 px-6 py-6 overflow-y-auto"
    >
      <span
        class="grid rounded-full size-10 place-content-center bg-n-iris-3 text-n-iris-11"
      >
        <Icon icon="i-lucide-sparkles" class="size-5" />
      </span>
      <div class="flex flex-col gap-1">
        <h2 class="m-0 text-base font-semibold text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.TITLE') }}
        </h2>
        <p class="m-0 text-sm text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex flex-col items-start gap-2">
        <button
          v-for="suggestion in suggestions"
          :key="suggestion"
          type="button"
          class="px-3 py-2 text-sm text-left transition-colors rounded-lg bg-n-alpha-2 text-n-slate-12 hover:bg-n-slate-4"
          @click="send(suggestion)"
        >
          {{ suggestion }}
        </button>
      </div>
    </div>
    <MessageList
      v-else
      :messages="messages"
      :is-loading="isLoading"
      class="pt-4"
    />
    <div
      v-if="failure"
      class="flex items-start gap-3 p-3 mx-4 mb-3 rounded-lg bg-n-ruby-2 outline outline-1 outline-n-ruby-5 -outline-offset-1"
    >
      <Icon
        icon="i-lucide-circle-alert"
        class="flex-shrink-0 size-4 mt-0.5 text-n-ruby-11"
      />
      <div class="flex flex-col flex-1 min-w-0 gap-0.5">
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.FAILED') }}
        </p>
        <p class="m-0 text-xs break-words text-n-slate-11">{{ failure }}</p>
      </div>
      <NextButton
        type="button"
        variant="faded"
        color="ruby"
        size="xs"
        icon="i-lucide-rotate-ccw"
        @click="requestDraft"
      >
        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.RETRY') }}
      </NextButton>
    </div>
    <!-- The input clears itself on Enter, so it takes nothing while a turn runs: a message sent then
         would vanish unanswered. -->
    <fieldset
      :disabled="isLoading"
      class="min-w-0 px-4 pb-4 disabled:opacity-60"
    >
      <CopilotInput
        :placeholder="t('WHATSAPP_TEMPLATE_MGMT.CREATE.ASSISTANT.PLACEHOLDER')"
        @send="send"
      />
    </fieldset>
  </section>
</template>
